import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import Global from '../store/global-selectors';

import { ANALYTICS_VIEW_TYPE } from '../pages/insights/constants';
import { useIsSellerOnFeatureGatedPlan } from './use-is-seller-on-feature-gated-plan';
import { useSellerIsInTrial } from '../hooks/use-seller-is-in-trial';
import { useIsSellerFeatureEnabled } from '../hooks/use-is-seller-feature-enabled';
import { Features } from '../constants/features';
import { useCookie } from '../hooks/use-cookie-value';
import { useEnableShipstationAnalyticsOverride } from '../feature-flags/hooks/use-enable-shipstation-analytics-override';
import { useIsDropIn } from '../hooks/use-is-drop-in';
import { useUserHasAnalyticsViewRole } from '../hooks/use-user-role';

const shipstationAnalyticsFeatureEnabledCookie = 'shipstation.intelligence.is.enabled';

const useIsShipStationAnalyticsFeatureEnabledByCookie = () => {
  const { cookieValue } = useCookie(shipstationAnalyticsFeatureEnabledCookie);
  return cookieValue === 'true';
};

/**
 * Hook to determine if the seller should see the new "Analytics" menu item and dashboards
 * @returns {boolean} True if the seller is on the GoodBetterBest plan profile, false otherwise
 */
export const useIsShipStationAnalyticsFeatureEnabled = () => {
  const isSellerInGoodBetterBestPlan = useIsSellerOnFeatureGatedPlan();
  const isEnabledByCookie = useIsShipStationAnalyticsFeatureEnabledByCookie();
  const isFeatureEnabled = useIsSellerFeatureEnabled(Features.ShipStationAnalytics);
  const isShipStationAnalyticsOverrideEnabled = useEnableShipstationAnalyticsOverride();

  return isSellerInGoodBetterBestPlan || isEnabledByCookie || isFeatureEnabled || isShipStationAnalyticsOverrideEnabled;
};

/**
 * Hook to determine what dashboards the seller should see based on their billing plan and account status.
 * @returns {string} The user's analytics view type (full, partial, or locked)
 */
export const useGetShipStationAnalyticsUserType = () => {
  const isDropIn = useIsDropIn();
  const isFeatureEnabled = useIsSellerFeatureEnabled(Features.ShipStationAnalytics);
  const hasCreatedLabel = useSelector(Global.selectSellerHasCreatedLabel);
  const isInTrial = useSellerIsInTrial();
  const isEnabledByCookie = useIsShipStationAnalyticsFeatureEnabledByCookie();
  const isShipStationAnalyticsOverrideEnabled = useEnableShipstationAnalyticsOverride();
  const userHasAnalyticsViewRole = useUserHasAnalyticsViewRole();

  return useMemo(() => {
    try {
      if (isEnabledByCookie || isShipStationAnalyticsOverrideEnabled) {
        return ANALYTICS_VIEW_TYPE.full;
      }

      if (!isFeatureEnabled) {
        return ANALYTICS_VIEW_TYPE.locked;
      }

      if (!userHasAnalyticsViewRole && !isDropIn) {
        // All users without View Role (except drop in) get partial access
        return ANALYTICS_VIEW_TYPE.partial;
      }

      if (!hasCreatedLabel && isInTrial) {
        // If the seller hasn't yet created a label and are in a trial, they don't get access
        return ANALYTICS_VIEW_TYPE.locked;
      }

      // If the user is the account owner _or_ the user is a drop in user, they get full access
      return ANALYTICS_VIEW_TYPE.full;
    } catch {
      return ANALYTICS_VIEW_TYPE.locked;
    }
  }, [
    isDropIn,
    userHasAnalyticsViewRole,
    hasCreatedLabel,
    isInTrial,
    isFeatureEnabled,
    isEnabledByCookie,
    isShipStationAnalyticsOverrideEnabled,
  ]);
};
